import { UserType, UserSetType } from '../types/user';
import { CompanyContextType } from '../contexts/auth';
import { companyPlan, planCreditRate, planLimits, OCR_UPLOAD_MAX_FILES } from '../constants/enum';

/**
 * プラン番号からクレジットレート（円/枚）を取得する
 * @param plan プラン番号
 * @returns クレジットレート
 */
export const getCreditRateByPlan = (plan: number): number => {
  switch (plan) {
    case companyPlan.free:
      return planCreditRate.free;
    case companyPlan.standard:
      return planCreditRate.standard;
    case companyPlan.pro:
      return planCreditRate.pro;
    case companyPlan.custom:
      return planCreditRate.custom;
    default:
      return planCreditRate.free;
  }
};

/**
 * 会社のプランに基づいたOCR利用枚数上限を取得する
 * @param company 会社情報（AuthContextから取得）
 * @returns OCR利用枚数上限
 */
export const getOcrLimitByCompany = (company: CompanyContextType | null): number => {
  // 会社情報がない場合はフリープランのデフォルト値
  if (!company) {
    return planLimits.free.defaultUsageLimit; // 5
  }

  // 会社の月間OCR予算とプランのクレジットレートから上限枚数を計算
  const creditRate = getCreditRateByPlan(company.plan);
  return Math.floor(company.ocr_monthly_budget / creditRate);
};

/**
 * ユーザーのOCR利用枚数上限を取得する
 * @deprecated getOcrLimitByCompany を使用してください
 * @param user ユーザー情報
 * @returns OCR利用枚数上限
 */
export const getUserUsageLimit = (user: UserType | UserSetType | null): number => {
  // ユーザーが存在しない場合はデフォルト値
  if (!user) {
    return planLimits.free.defaultUsageLimit;
  }

  // UserTypeの場合、ユーザー固有の設定があればそれを使用
  if ('usage_limit' in user && user.usage_limit != null && user.usage_limit > 0) {
    return user.usage_limit;
  }

  // 未設定の場合はフリープランのデフォルト値
  return planLimits.free.defaultUsageLimit;
};

/**
 * 個人別 月間OCRページ上限（#2804 Phase4）を取得する
 * - `users.usage_limit` を月間OCRページ上限として解釈する
 * - 未設定 / null / 0以下 は「無制限」とみなし null を返す
 * @param user ユーザー情報
 * @returns 月間上限ページ数。無制限の場合は null
 */
export const getUserMonthlyOcrLimit = (user: UserType | UserSetType | null): number | null => {
  if (!user) return null;
  if ('usage_limit' in user && typeof user.usage_limit === 'number' && user.usage_limit > 0) {
    return user.usage_limit;
  }
  return null;
};

/**
 * 個人別 月間OCRページ上限を超過するか判定する（#2804 Phase4）
 * @param limit 月間上限ページ数（null=無制限）
 * @param usedPages 当月の利用済みページ数
 * @param addPages 今回追加するページ数
 * @returns 超過する場合 true
 */
export const isUserMonthlyOcrLimitExceeded = (
  limit: number | null,
  usedPages: number,
  addPages: number,
): boolean => {
  if (limit === null) return false; // 無制限
  return usedPages + addPages > limit;
};

/**
 * 個人別 月間OCR上限の超過メッセージを生成する（#3133）
 * 会社枠のメッセージと同じくファイル選択時のインライン表示に使う。
 * @param limit 月間上限ページ数（null=無制限）
 * @param usedPages 当月の利用済みページ数
 * @param addPages 今回追加するページ数
 * @returns 超過時のメッセージ。超過しない場合は null
 */
export const getUserMonthlyOcrLimitMessage = (
  limit: number | null,
  usedPages: number,
  addPages: number,
): string | null => {
  if (!isUserMonthlyOcrLimitExceeded(limit, usedPages, addPages)) return null;
  const remaining = Math.max(0, (limit as number) - usedPages);
  return remaining <= 0
    ? `ユーザー毎に設定された月間OCR上限（${limit}ページ）に達したため実行できません。企業管理者にご連絡ください。`
    : `ユーザー毎に設定された月間OCR上限（${limit}ページ）を超えています。あと${remaining}ページまで処理可能です。`;
};

/**
 * OCRクレジット情報の計算結果
 */
export interface OcrLimitInfo {
  /** 月間枠の残り枚数 */
  remainingCount: number;
  /** OCRクレジットから追加可能な枚数 */
  creditFileCount: number;
  /** 合計アップロード可能枚数（月間枠 + OCRクレジット） */
  totalRemainingCount: number;
  /** プランに応じたクレジットレート */
  creditRate: number;
  /** OCRクレジット残高（円） */
  creditBalance: number;
}

/**
 * OCRアップロード可能枚数を計算する
 * 月間枠の残り枚数とOCRクレジットから追加可能な枚数を計算
 * @param company 会社情報
 * @param baseRemainingCount DB上の月間枠残り枚数
 * @returns OCR制限情報
 */
export const calculateOcrLimitInfo = (
  company: CompanyContextType | null,
  baseRemainingCount: number | null,
): OcrLimitInfo => {
  // デフォルト値
  if (!company || baseRemainingCount === null) {
    return {
      remainingCount: 0,
      creditFileCount: 0,
      totalRemainingCount: 0,
      creditRate: planCreditRate.free,
      creditBalance: 0,
    };
  }

  const remainingCount = Math.max(0, baseRemainingCount);
  const creditRate = getCreditRateByPlan(company.plan);
  const creditBalance = company.ocr_credit_balance ?? 0;
  const creditFileCount = Math.max(0, Math.floor(creditBalance / creditRate));
  const totalRemainingCount = remainingCount + creditFileCount;

  return {
    remainingCount,
    creditFileCount,
    totalRemainingCount,
    creditRate,
    creditBalance,
  };
};

/**
 * 会社の月間枠・OCRクレジットに対する超過メッセージを生成する（#3133）
 *
 * 判定はファイル数ではなくページ数で行っているため、
 * 「ファイル数を減らしてください」ではなくページ数を基準に案内する。
 *
 * @param totalPageCount 選択中の合計ページ数
 * @param limitInfo OCR制限情報
 * @returns 超過時のメッセージ。超過しない場合は null
 */
export const getOcrLimitOverMessage = (
  totalPageCount: number,
  limitInfo: OcrLimitInfo,
): string | null => {
  if (totalPageCount <= limitInfo.totalRemainingCount) return null;

  // 月間枠・クレジットともに残りがない
  if (limitInfo.totalRemainingCount <= 0) {
    return '今月の利用上限に達しています。追加枠をご希望の場合は事務局にお問い合わせください。';
  }

  // 月間枠を使い切り、クレジット分だけ残っている
  if (limitInfo.remainingCount <= 0) {
    return `今月の無料利用枠を使い切りました。OCRクレジット残高（${limitInfo.creditBalance}円）であと${limitInfo.creditFileCount}枚まで読み取れます。選択中は${totalPageCount}枚です。`;
  }

  // 月間枠 + クレジットの合計を超えている
  return `今月読み取れるのはあと${limitInfo.totalRemainingCount}枚（無料枠${limitInfo.remainingCount}枚 + OCRクレジット${limitInfo.creditFileCount}枚）です。選択中は${totalPageCount}枚です。`;
};

/**
 * OCRクレジットから消費される旨の案内メッセージを生成する（#3133）
 *
 * 無料枠に収まらないが合計では足りている場合に、
 * 「無料枠を使い切ってから超過分だけクレジットを使う」ことを明示する。
 * 実際の消費枚数は文字量によりOCR後に増えることがあるため、枚数は断定せずに案内する。
 *
 * @param totalPageCount 選択中の合計ページ数
 * @param limitInfo OCR制限情報
 * @returns 案内メッセージ。該当しない場合は null
 */
export const getOcrCreditUsageMessage = (
  totalPageCount: number,
  limitInfo: OcrLimitInfo,
): string | null => {
  if (totalPageCount > limitInfo.totalRemainingCount) return null;
  if (totalPageCount <= limitInfo.remainingCount) return null;
  if (limitInfo.creditFileCount <= 0) return null;

  // 残高は枚数ではなく円で示す（消費枚数と並ぶと消費量に見えるため / #3133）。
  // 消費枚数は文字量によりOCR後に増える可能性があるため、具体的な枚数は断定しない。
  if (limitInfo.remainingCount <= 0) {
    return `今月の無料利用枠を使い切っているため、読み取る枚数分をOCRクレジット残高（${limitInfo.creditBalance}円）から消費します。`;
  }
  return `今月の無料利用枠の残り${limitInfo.remainingCount}枚を使用し、超過する枚数分をOCRクレジット残高（${limitInfo.creditBalance}円）から消費します。`;
};

/**
 * デモOCR用の説明テキスト用ファイルアップロード上限枚数を取得する
 * - フリープラン + OCRクレジットなし → 5枚
 * - フリープラン + OCRクレジットあり → 30枚
 * - その他のプラン → 30枚
 * @param company 会社情報
 * @returns 説明テキストに表示するファイル上限枚数
 */
export const getDisplayUploadLimit = (company: CompanyContextType | null): number => {
  const maxFilesPerUpload = OCR_UPLOAD_MAX_FILES; // 一度の処理上限

  if (!company) {
    return planLimits.free.defaultUsageLimit; // 5
  }

  // フリープランの場合
  if (company.plan === companyPlan.free) {
    // OCRクレジットがあれば30枚、なければ5枚
    if (company.ocr_credit_balance > 0) {
      return maxFilesPerUpload;
    }
    return planLimits.free.defaultUsageLimit; // 5
  }

  // その他のプランは30枚
  return maxFilesPerUpload;
};
